import React, { useState } from 'react';
import { ChevronDown, ChevronRight, BookOpen, Target, Lightbulb, AlertTriangle, Clock } from 'lucide-react';

const LinguisticAnalysis = ({ analysisData, originalSentence }) => {
  const [expandedSections, setExpandedSections] = useState({
    mainClause: true,
    subordinateClauses: true,
    rewriteOptions: false
  });

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const renderLegendStructure = (structure, pattern) => {
    if (!structure) return null;
    
    // Parse the legend structure to highlight parts
    const parts = structure.match(/(\w+)\(([SVOC])\)/g);
    if (!parts) return <span className="text-gray-700">{structure}</span>;

    return (
      <div className="flex flex-wrap gap-1 items-center">
        {structure.split(/(\w+\([SVOC]\))/).map((part, index) => {
          const match = part.match(/(\w+)\(([SVOC])\)/);
          if (match) {
            const [, word, label] = match;
            return (
              <span key={index} className="inline-flex items-center gap-1">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm font-medium">
                  {word}
                </span>
                <span className="text-blue-600 font-bold text-xs">({label})</span>
              </span>
            );
          }
          return part ? <span key={index} className="text-gray-700">{part}</span> : null;
        })}
      </div>
    );
  };

  const renderSubordinateClause = (clause) => {
    if (!clause) return null;

    return (
      <div className="bg-gray-50 border-l-4 border-orange-400 p-4 rounded-r-lg">
        <div className="flex items-center gap-2 mb-3">
          <ChevronDown className="w-4 h-4 text-orange-600" />
          <span className="text-orange-600 font-semibold">條件從句 (Conditional Clause)</span>
          <span className="text-orange-500 text-sm">/ Subordinate Clause</span>
        </div>
        
        <div className="bg-white p-3 rounded border">
          <div className="mb-2">
            <span className="font-medium text-gray-700">Clause:</span>
            <span className="ml-2 text-blue-600 font-medium">"{clause.clause}"</span>
          </div>
          
          <div className="mb-2">
            <span className="font-medium text-gray-700">Pattern:</span>
            <span className="ml-2 bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-medium">
              {clause.pattern}
            </span>
          </div>
          
          <div className="mb-2">
            <span className="font-medium text-gray-700">Legend Structure:</span>
            <div className="mt-1">
              {renderLegendStructure(
                `${clause.analysis.subject}(S) + ${clause.analysis.verb}(V) + ${clause.analysis.directObject}(O)`,
                clause.pattern
              )}
            </div>
          </div>
          
          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
            <pre className="whitespace-pre-wrap">
              {JSON.stringify(clause, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    );
  };

  const renderRewriteOptions = (options) => {
    if (!options) return null;

    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-purple-800 font-semibold mb-3 flex items-center gap-2">
          <Target className="w-4 h-4" />
          Rewrite Options
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Object.entries(options).map(([pattern, example]) => (
            <div key={pattern} className="bg-white p-3 rounded border border-purple-200">
              <div className="font-medium text-purple-700 mb-1">{pattern}:</div>
              <div className="text-gray-700 italic">"{example}"</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!analysisData) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertTriangle className="w-5 h-5" />
          <span>No analysis data provided</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Original Sentence */}
      <div className="bg-blue-50 border-b border-blue-200 p-4">
        <h3 className="text-blue-800 font-semibold mb-2">原文句子 (Original Sentence):</h3>
        <p className="text-blue-700 text-lg font-medium">
          "{originalSentence || 'With my sporting background and experience, I believe that I have the interest, dedication and necessary experience for your trainee reporter vacancy'}"
        </p>
      </div>

      {/* Main Clause Analysis */}
      <div className="p-4 border-b border-gray-200">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => toggleSection('mainClause')}
        >
          <h4 className="text-gray-800 font-semibold flex items-center gap-2">
            {expandedSections.mainClause ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            主句句型 (Main Clause Pattern):
          </h4>
        </div>
        
        {expandedSections.mainClause && (
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded font-medium">
                {analysisData.mainPattern || 'S+V+O'}
              </span>
              <span className="text-gray-700">
                {analysisData.basicStructure || 'Subject + Verb + Object'}
              </span>
            </div>
            
            <div>
              <div className="font-medium text-gray-700 mb-2">Legend結構 (Legend Structure):</div>
              <div className="bg-blue-50 p-3 rounded">
                {renderLegendStructure(analysisData.legendStructure)}
              </div>
            </div>
            
            <div>
              <div className="font-medium text-gray-700 mb-2">細分 (Detailed Breakdown):</div>
              <div className="text-gray-600">
                {Object.entries(analysisData.detailedAnalysis || {}).map(([key, value]) => (
                  <div key={key} className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-700">{key}:</span>
                    <span className="text-gray-600">{value || 'null'}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <div className="font-medium text-gray-700 mb-2">細細分 (Further Detailed Breakdown):</div>
              <div className="text-gray-600">
                {(analysisData.specialFeatures || []).join('、')}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Subordinate Clauses */}
      {analysisData.subordinateClauses && analysisData.subordinateClauses.length > 0 && (
        <div className="p-4 border-b border-gray-200">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('subordinateClauses')}
          >
            <h4 className="text-gray-800 font-semibold flex items-center gap-2">
              {expandedSections.subordinateClauses ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              條件從句 (Subordinate Clauses)
            </h4>
          </div>
          
          {expandedSections.subordinateClauses && (
            <div className="mt-3 space-y-3">
              {analysisData.subordinateClauses.map((clause, index) => (
                <div key={index}>
                  {renderSubordinateClause(clause)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Verb Tense */}
      {analysisData.verbTense && (
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-green-600" />
            <span className="font-medium text-gray-700">Verb Tense:</span>
            <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
              {analysisData.verbTense}
            </span>
          </div>
        </div>
      )}

      {/* Rewrite Options */}
      {analysisData.rewriteOptions && (
        <div className="p-4">
          <div 
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection('rewriteOptions')}
          >
            <h4 className="text-gray-800 font-semibold flex items-center gap-2">
              {expandedSections.rewriteOptions ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <Lightbulb className="w-4 h-4 text-purple-600" />
              Rewrite Options
            </h4>
          </div>
          
          {expandedSections.rewriteOptions && (
            <div className="mt-3">
              {renderRewriteOptions(analysisData.rewriteOptions)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LinguisticAnalysis; 